import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listCreditMemos, createCreditMemo, updateCreditMemoStatus, getCreditMemo,
} from "@/lib/credit-memos.functions";
import { listRmas } from "@/lib/returns.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, FilePlus2, Trash2, Plus } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/returns-credits")({ component: CreditsPage });

const STATUSES = ["draft","issued","applied","void"] as const;
const STATUS_VARIANT: Record<string, "secondary"|"default"|"outline"|"destructive"> = {
  draft: "secondary", issued: "default", applied: "outline", void: "destructive",
};

type DraftLine = { description: string; qty: number; unitPriceCents: number };

function CreditsPage() {
  const qc = useQueryClient();
  const { tenantId, facilityId, customerId, customers } = useScope();
  const listFn = useServerFn(listCreditMemos);
  const createFn = useServerFn(createCreditMemo);
  const statusFn = useServerFn(updateCreditMemoStatus);
  const getFn = useServerFn(getCreditMemo);
  const listRmasFn = useServerFn(listRmas);

  const [statusFilter, setStatusFilter] = useState<typeof STATUSES[number] | "all">("all");
  const { data } = useQuery({
    queryKey: ["credit-memos", tenantId, facilityId, customerId, statusFilter],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null, customerId: customerId ?? null, status: statusFilter } }),
  });

  const cName = (id: string) => customers.find((c) => c.id === id)?.name ?? id.slice(0,8);

  // Create
  const [open, setOpen] = useState(false);
  const [cust, setCust] = useState(customerId ?? "");
  const [memoNum, setMemoNum] = useState("");
  const [rmaId, setRmaId] = useState<string>("");
  const [lines, setLines] = useState<DraftLine[]>([{ description: "", qty: 1, unitPriceCents: 0 }]);

  const { data: rmas } = useQuery({
    queryKey: ["rmas-for-credit", tenantId, facilityId, cust],
    enabled: open && !!tenantId && !!facilityId,
    queryFn: () => listRmasFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: cust || null } }),
  });

  const create = useMutation({
    mutationFn: () => createFn({ data: {
      tenantId: tenantId!, customerId: cust,
      rmaId: rmaId || null, memoNumber: memoNum.trim(),
      currency: "USD",
      lines: lines.filter((l) => l.description.trim() && l.qty > 0).map((l) => ({
        description: l.description.trim(), qty: l.qty, unitPriceCents: l.unitPriceCents,
      })),
    } }),
    onSuccess: () => {
      toast.success("Credit memo created");
      setOpen(false); setMemoNum(""); setRmaId(""); setLines([{ description: "", qty: 1, unitPriceCents: 0 }]);
      qc.invalidateQueries({ queryKey: ["credit-memos"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: typeof STATUSES[number] }) => statusFn({ data: v }),
    onSuccess: (_d, v) => {
      toast.success("Status updated");
      qc.invalidateQueries({ queryKey: ["credit-memos"] });
      qc.invalidateQueries({ queryKey: ["credit-memo", v.id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Detail
  const [viewing, setViewing] = useState<string | null>(null);
  const { data: detail } = useQuery({
    queryKey: ["credit-memo", viewing],
    enabled: !!viewing,
    queryFn: () => getFn({ data: { id: viewing! } }),
  });

  const subtotal = lines.reduce((s, l) => s + (l.qty * l.unitPriceCents), 0);

  return (
    <>
      <PageHeader
        title="Credit Memos"
        description="Formal credit issued to brands for returns. Track lifecycle from draft to applied."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm"><FilePlus2 className="h-4 w-4 mr-1" />New memo</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>New credit memo</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Customer</Label>
                    <Select value={cust} onValueChange={setCust}>
                      <SelectTrigger><SelectValue placeholder="Pick customer" /></SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Memo #</Label>
                    <Input value={memoNum} onChange={(e) => setMemoNum(e.target.value)} placeholder="CM-001" />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs text-muted-foreground">RMA (optional)</Label>
                    <Select value={rmaId || "none"} onValueChange={(v) => setRmaId(v === "none" ? "" : v)}>
                      <SelectTrigger><SelectValue placeholder="Link to RMA" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {(rmas ?? []).filter((r: any) => !cust || r.customer_id === cust).map((r: any) => (
                          <SelectItem key={r.id} value={r.id}>{r.rma_number}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Line items</Label>
                    <Button size="sm" variant="ghost" onClick={() => setLines((ls) => [...ls, { description: "", qty: 1, unitPriceCents: 0 }])}>
                      <Plus className="h-3.5 w-3.5 mr-1" />Add line
                    </Button>
                  </div>
                  {lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-6" placeholder="Description" value={l.description}
                        onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                      <Input className="col-span-2" type="number" min={1} value={l.qty}
                        onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, qty: Math.max(1, Number(e.target.value) || 1) } : x))} />
                      <Input className="col-span-3" type="number" min={0} placeholder="Unit ¢" value={l.unitPriceCents}
                        onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, unitPriceCents: Math.max(0, Number(e.target.value) || 0) } : x))} />
                      <Button size="icon" variant="ghost" className="col-span-1"
                        onClick={() => setLines((ls) => ls.length > 1 ? ls.filter((_, j) => j !== i) : ls)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="text-right text-sm">Subtotal: <span className="tabular-nums font-medium">${(subtotal / 100).toFixed(2)}</span></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={() => create.mutate()} disabled={!tenantId || !cust || !memoNum.trim() || create.isPending}>Create draft</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/app/returns"><ArrowLeft className="h-4 w-4 mr-1" />Back to returns</Link>
          </Button>
        </div>

        <Card>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="text-xs text-muted-foreground">Status</div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Memo #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.memos ?? []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No credit memos.</TableCell></TableRow>}
                {(data?.memos ?? []).map((m: any) => (
                  <TableRow key={m.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setViewing(m.id)}>
                    <TableCell className="font-medium">{m.memo_number}</TableCell>
                    <TableCell className="text-sm">{cName(m.customer_id)}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[m.status]}>{m.status}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">${(m.total_cents / 100).toFixed(2)} {m.currency}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{m.issued_at ? new Date(m.issued_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {m.status === "draft" && <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: m.id, status: "issued" })}>Issue</Button>}
                      {m.status === "issued" && <Button size="sm" onClick={() => setStatus.mutate({ id: m.id, status: "applied" })}>Mark applied</Button>}
                      {(m.status === "draft" || m.status === "issued") && <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: m.id, status: "void" })}>Void</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{detail?.memo?.memo_number ?? "Memo"}</DialogTitle></DialogHeader>
            {detail && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Badge variant={STATUS_VARIANT[detail.memo.status]}>{detail.memo.status}</Badge>
                  <span className="text-muted-foreground">{cName(detail.memo.customer_id)}</span>
                  <span className="ml-auto tabular-nums font-medium">${(detail.memo.total_cents / 100).toFixed(2)} {detail.memo.currency}</span>
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Description</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Unit</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {detail.lines.map((l: any) => (
                      <TableRow key={l.id}>
                        <TableCell>{l.description}</TableCell>
                        <TableCell className="text-right tabular-nums">{l.qty}</TableCell>
                        <TableCell className="text-right tabular-nums">${(l.unit_price_cents / 100).toFixed(2)}</TableCell>
                        <TableCell className="text-right tabular-nums">${(l.amount_cents / 100).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {detail.memo.notes && <div className="text-xs text-muted-foreground">{detail.memo.notes}</div>}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}